import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowRight, BadgeCheck, BarChart3, CalendarDays, CheckCircle2, ChevronLeft, ClipboardList, Globe,
  KeyRound, MessageCircle, MessageSquare, Package, Send, ShoppingBag, Sparkles, X
} from 'lucide-react';
import {
  Alert, AsyncBoundary, Badge, Button, Card, CardHeader, ConfirmDialog, Field,
  Input, Modal, SectionTitle, Select, StatCard, StatGrid, Tabs, Textarea
} from '../ui';
import {
  CHANGE_TYPES, PRIORITIES, REQUEST_STATUS, SERVICE_STATUS, formatDate,
  formatDateTime, formatMoney, portalService
} from '../../services/agencyService';
import API_URL from '../../config/api';
import { socialUrlWithToken } from '../../config/social';

// ⭐ Mi Proyecto — portal del cliente (spec §1).
//
// Todo el diseño de esta pantalla parte de una restricción de la spec: el
// cliente NO ve una interfaz técnica. En concreto eso significa que aquí:
//
//  · no hay ningún botón que aplique un cambio — solo se pueden PEDIR cambios;
//  · no se muestran identificadores internos, workflows, tokens ni credenciales;
//  · los estados se traducen a lenguaje humano ("Trabajando en ello", no
//    "in_progress") usando `clientLabel` del vocabulario compartido;
//  · no existe forma de consultar datos de otro cliente: el backend responde
//    únicamente con lo del cliente de la sesión.
//
// El mensaje que la spec exige mostrar ("Tu solicitud será revisada…") está
// en el formulario, visible antes de enviar, no escondido en un aviso posterior.

const AVISO_APROBACION = 'Tu solicitud será revisada por MARKETOS. Una vez aprobada, nuestro equipo realizará el cambio y te avisará cuando esté listo.';
const BILLING_CYCLE_LABEL = { one_time: 'pago único', monthly: 'al mes', annual: 'al año' };
const DOMAIN_STATUS_LABEL = {
  pending: 'Pendiente', reserved: 'Reservado', configuring: 'Configurando',
  active: 'Activo', expired: 'Vencido'
};
const safeHttpUrl = (value) => /^https?:\/\//i.test(String(value || '')) ? value : null;
const MARKETOS_WHATSAPP = '593997125285';
const CUSTOM_ANSWER = '__marketos_custom_answer__';
const WEB_PROJECT_PROGRESS = {
  setup: 10,
  waiting_content: 20,
  ready_to_build: 35,
  building: 50,
  quality_review: 65,
  client_review: 75,
  approved: 82,
  domain_setup: 90,
  published: 100,
  warranty: 100,
  complete: 100,
  paused: 10
};

// Presentación comercial del catálogo. La información contractual sigue
// viniendo del backend; aquí se adapta el mensaje para hablarle al dueño del
// negocio desde su problema y el resultado que quiere conseguir.
const SERVICE_PRESENTATION = {
  'landing-basica': {
    image: 'https://images.unsplash.com/photo-1556740749-887f6717d7e4?auto=format&fit=crop&w=1200&q=82',
    eyebrow: 'Haz que te encuentren y confíen',
    promise: 'Tu negocio merece verse tan profesional como el esfuerzo que pones cada día.',
    result: 'Reúne lo mejor de tu negocio en una página clara que lleva a tus clientes directo a WhatsApp.',
    cta: 'Quiero verme profesional'
  },
  'landing-captacion': {
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1200&q=82',
    eyebrow: 'Convierte visitas en oportunidades',
    promise: 'Deja de perder a las personas que preguntan una vez y después desaparecen.',
    result: 'Captura sus datos, ordénalos y empieza cada conversación con seguimiento desde el primer momento.',
    cta: 'Quiero recibir más clientes'
  },
  'landing-comercial': {
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=82',
    eyebrow: 'Vende con un proceso más claro',
    promise: 'No vuelvas a explicar tu oferta desde cero en cada conversación.',
    result: 'Presenta beneficios, responde dudas y guía a cada interesado hasta el siguiente paso de compra.',
    cta: 'Quiero vender con más orden'
  },
  'sistema-captacion': {
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=82',
    eyebrow: 'Tu sistema comercial trabajando contigo',
    promise: 'Recupera el control sin perseguir mensajes, contactos y oportunidades por separado.',
    result: 'Conecta página, WhatsApp, CRM y seguimiento para saber quién llegó, qué necesita y qué hacer después.',
    cta: 'Quiero ordenar mis ventas'
  }
};

const getServicePresentation = (service) => SERVICE_PRESENTATION[service.slug] || {
  image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=82',
  eyebrow: 'Una solución para avanzar',
  promise: `Haz que ${service.name} se convierta en una mejora real para tu negocio.`,
  result: service.description,
  cta: `Quiero conocer ${service.name}`
};

const serviceWhatsAppUrl = (service, businessName) => {
  const nameLine = businessName ? ` Soy parte de ${businessName}.` : '';
  const message = `Hola, estaba revisando los productos dentro de mi interfaz de MARKETOS y me interesa ${service.name}.${nameLine} Quisiera saber cómo puede ayudar a mi negocio y recibir más información.`;
  return `https://wa.me/${MARKETOS_WHATSAPP}?text=${encodeURIComponent(message)}`;
};

const EMPTY_FORM = {
  service_id: '', change_type: 'texto', title: '', description: '',
  reason: '', priority: 'normal', desired_date: '', attachments: ''
};

export default function MiProyecto({ clientData }) {
  const [tab, setTab] = useState('resumen');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantServiceId, setAssistantServiceId] = useState('');
  const [assistantGoal, setAssistantGoal] = useState('');
  const [assistantIntake, setAssistantIntake] = useState(null);
  const [assistantAnswers, setAssistantAnswers] = useState({});
  const [assistantCustomAnswers, setAssistantCustomAnswers] = useState({});
  const [assistantStep, setAssistantStep] = useState(0);
  const [assistantSubmissionError, setAssistantSubmissionError] = useState('');
  const [detailRequest, setDetailRequest] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [reply, setReply] = useState('');
  const [workflowRecipient, setWorkflowRecipient] = useState(null);
  const [recipientMode, setRecipientMode] = useState('registered');
  const [customRecipient, setCustomRecipient] = useState('');
  const [aiConnection, setAiConnection] = useState(null);
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [disconnectingAi, setDisconnectingAi] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    if (!silent) setError(null);
    try {
      const summary = await portalService.summary();
      setData(summary);
      if (summary.client?.product_type === 'crm') {
        const destination = await portalService.workflowNotificationRecipient().catch(() => null);
        if (destination) {
          setWorkflowRecipient(destination);
          setRecipientMode(destination.mode || 'registered');
        }
      } else {
        setWorkflowRecipient(null);
      }
      const hasActiveCrmAutomation = (summary.services || []).some((service) => service.status === 'active' && service.category === 'crm');
      if (hasActiveCrmAutomation) {
        setAiConnection(await portalService.aiConnection().catch(() => null));
      } else {
        setAiConnection(null);
      }
      // Si el cliente tiene abierto el detalle de una solicitud, mantenerlo
      // sincronizado con la versión recién recibida para que vea el cambio de
      // estado sin cerrar el modal ni recargar el navegador.
      setDetailRequest((current) => current
        ? (summary.requests || []).find((request) => request.id === current.id) || current
        : null);
    } catch (err) {
      if (!silent) setError(err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = () => load({ silent: true });
    const interval = setInterval(refresh, 20000);
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', refresh);
    };
  }, [load]);

  const services = data?.services || [];
  const availableServices = data?.available_services || [];
  const requests = data?.requests || [];
  const messages = data?.messages || [];
  const metrics = data?.metrics || {};

  const openRequests = requests.filter((r) => ['pending', 'reviewing', 'approved', 'in_progress', 'returned'].includes(r.status));
  const needsMyAnswer = requests.filter((r) => r.status === 'returned');
  const isServicesOnly = data?.client?.product_type === 'services' || clientData?.productType === 'services';
  const hasCrm = data?.client?.product_type === 'crm' || clientData?.productType === 'crm';
  const hasActiveCrmAutomation = services.some((service) => service.status === 'active' && service.category === 'crm');

  const openMarketosSocial = () => {
    const sessionToken = sessionStorage.getItem('token');
    const socialUrl = socialUrlWithToken(sessionToken);
    if (!socialUrl) {
      setNotice({ tone: 'danger', text: 'No pudimos abrir MARKETOS Social con tu sesión actual. Vuelve a iniciar sesión e inténtalo de nuevo.' });
      return;
    }
    window.location.assign(socialUrl);
  };

  const run = async (fn, successMessage) => {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      setNotice({ tone: 'success', text: successMessage });
      await load();
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const saveWorkflowRecipient = async () => {
    await run(async () => {
      const saved = await portalService.saveWorkflowNotificationRecipient({
        mode: recipientMode,
        whatsapp: recipientMode === 'custom' ? customRecipient : undefined
      });
      setWorkflowRecipient(saved);
      setCustomRecipient('');
    }, 'Destino de avisos guardado. Se usará en las nuevas automatizaciones que confirmes.');
  };

  const saveAiConnection = async () => {
    const apiKey = aiApiKey.trim();
    if (!apiKey) {
      setNotice({ tone: 'danger', text: 'Escribe tu API key antes de guardarla.' });
      return;
    }
    setAiBusy(true);
    setNotice(null);
    try {
      const saved = await portalService.saveAiConnection(apiKey);
      setAiConnection(saved);
      setAiApiKey('');
      setNotice({ tone: 'success', text: saved.message || 'Tu API key quedó guardada.' });
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
    } finally {
      setAiBusy(false);
    }
  };

  const testAiConnection = async () => {
    setAiBusy(true);
    setNotice(null);
    try {
      const result = await portalService.testAiConnection();
      setNotice(result.success
        ? { tone: 'success', text: result.message || 'Conexión lista.' }
        : { tone: 'danger', text: result.error || 'No pudimos validar la conexión.' });
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
    } finally {
      setAiBusy(false);
    }
  };

  const disconnectAiConnection = async () => {
    setAiBusy(true);
    setNotice(null);
    try {
      const result = await portalService.disconnectAiConnection();
      setAiConnection(result);
      setAiApiKey('');
      setDisconnectingAi(false);
      setNotice({ tone: 'success', text: result.message || 'Desconectamos tu API key.' });
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
    } finally {
      setAiBusy(false);
    }
  };

  const submitRequest = async () => {
    // Validación antes de enviar: es mucho mejor señalar el campo vacío que
    // dejar que el backend devuelva un 400 genérico.
    const errors = {};
    if (!form.title.trim()) errors.title = 'Cuéntanos en pocas palabras qué quieres cambiar.';
    if (!form.description.trim()) errors.description = 'Describe cómo te gustaría que quedara.';
    if (!form.service_id) errors.service_id = 'Selecciona primero el producto contratado que quieres ajustar.';
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    await run(async () => {
      await portalService.createRequest({
        title: form.title.trim(),
        description: form.description.trim(),
        reason: form.reason.trim(),
        priority: form.priority,
        change_type: form.change_type,
        desired_date: form.desired_date || null,
        service_id: form.service_id ? Number(form.service_id) : null,
        attachments: form.attachments.split('\n').map((s) => s.trim()).filter(Boolean)
      });
      setForm(EMPTY_FORM);
      setFormOpen(false);
    }, 'Recibimos tu solicitud. Te avisaremos en cuanto la revisemos.');
  };

  const prepareAssistantForm = async () => {
    if (!assistantServiceId) {
      setAssistantSubmissionError('Elige primero el producto contratado sobre el que quieres solicitar el cambio.');
      return;
    }
    if (!assistantGoal.trim()) {
      setAssistantSubmissionError('Cuéntanos primero qué necesitas crear o cambiar.');
      return;
    }
    setBusy(true);
    setNotice(null);
    setAssistantSubmissionError('');
    try {
      const result = await portalService.startAiIntake(assistantGoal.trim(), Number(assistantServiceId));
      setAssistantIntake(result.intake);
      setAssistantAnswers({});
      setAssistantCustomAnswers({});
      setAssistantStep(0);
    } catch (err) {
      setAssistantSubmissionError(err.message || 'No pudimos revisar si este cambio corresponde a tu producto.');
    } finally {
      setBusy(false);
    }
  };

  const submitAssistantIntake = async () => {
    if (!assistantIntake) return;
    setBusy(true);
    setNotice(null);
    setAssistantSubmissionError('');
    try {
      const answers = Object.fromEntries((assistantIntake.requirement_form?.fields || []).flatMap((field) => {
        const value = assistantAnswers[field.key] === CUSTOM_ANSWER
          ? assistantCustomAnswers[field.key]
          : (assistantAnswers[field.key] ?? defaultAssistantAnswer(field));
        return String(value || '').trim() ? [[field.key, String(value).trim()]] : [];
      }));
      await portalService.submitAiIntake(assistantIntake.id, answers);
      setAssistantOpen(false);
      setAssistantGoal('');
      setAssistantIntake(null);
      setAssistantAnswers({});
      setAssistantCustomAnswers({});
      setAssistantStep(0);
      setNotice({ tone: 'success', text: 'Preparamos tu propuesta. MARKETOS la revisará antes de realizar cualquier cambio.' });
      await load();
    } catch (err) {
      // No cerramos el modal si la propuesta no se pudo preparar: el cliente
      // conserva sus respuestas y ve la explicación en el mismo lugar.
      setAssistantSubmissionError(err.message || 'No pudimos preparar la propuesta todavía.');
    } finally {
      setBusy(false);
    }
  };

  const assistantFields = assistantIntake?.requirement_form?.fields || [];
  const currentAssistantField = assistantFields[assistantStep] || null;
  // Solo la IA global puede proponer un borrador contextual. Nunca se rellena
  // un texto genérico de bienvenida por heurística del navegador.
  const defaultAssistantAnswer = (field) => field?.default_value || '';
  const assistantFieldExample = (field) => {
    if (field?.example) return field.example;
    const title = `${field?.key || ''} ${field?.label || ''}`.toLowerCase();
    if (/accion|respuesta|seguimiento/.test(title)) return 'Ej.: Enviar un último mensaje a las 24 horas y avisar a (responsable) para que haga una llamada manual.';
    if (/etapa|embudo|pipeline/.test(title)) return 'Ej.: Nuevo interesado → En conversación → Seguimiento → Cliente.';
    if (/dato|segment/.test(title)) return 'Ej.: Interés, producto consultado y ubicación.';
    return '';
  };
  const currentAssistantAnswer = currentAssistantField
    ? (assistantAnswers[currentAssistantField.key] === CUSTOM_ANSWER
      ? assistantCustomAnswers[currentAssistantField.key]
      : (assistantAnswers[currentAssistantField.key] ?? defaultAssistantAnswer(currentAssistantField)))
    : '';
  const goToNextAssistantStep = () => {
    if (!currentAssistantField) return;
    if (currentAssistantField.required && !String(currentAssistantAnswer || '').trim()) {
      setNotice({ tone: 'danger', text: `Completa: ${currentAssistantField.label}.` });
      return;
    }
    setNotice(null);
    setAssistantStep((step) => Math.min(step + 1, assistantFields.length - 1));
  };
  const closeAssistant = () => {
    if (busy) return;
    setAssistantOpen(false);
    setAssistantServiceId('');
    setAssistantGoal('');
    setAssistantIntake(null);
    setAssistantAnswers({});
    setAssistantCustomAnswers({});
    setAssistantStep(0);
    setAssistantSubmissionError('');
  };
  const openAssistant = (serviceId = '') => {
    setAssistantServiceId(serviceId ? String(serviceId) : '');
    setAssistantGoal('');
    setAssistantIntake(null);
    setAssistantAnswers({});
    setAssistantCustomAnswers({});
    setAssistantStep(0);
    setAssistantSubmissionError('');
    setAssistantOpen(true);
  };
  const assistantService = services.find((service) => String(service.assignment_id) === String(assistantServiceId));

  const TABS = [
    { id: 'resumen', label: 'Resumen', icon: <Sparkles className="h-4 w-4" /> },
    { id: 'productos', label: isServicesOnly ? 'Mis servicios' : 'Mis productos', icon: <Package className="h-4 w-4" />, count: services.length },
    ...(hasActiveCrmAutomation ? [{ id: 'ia', label: 'Conectar mi IA', icon: <KeyRound className="h-4 w-4" /> }] : []),
    { id: 'catalogo', label: 'Explorar servicios', icon: <ShoppingBag className="h-4 w-4" />, count: availableServices.length },
    { id: 'solicitudes', label: 'Mis solicitudes', icon: <ClipboardList className="h-4 w-4" />, count: openRequests.length },
    { id: 'metricas', label: 'Mis resultados', icon: <BarChart3 className="h-4 w-4" /> },
    ...(hasCrm ? [{ id: 'avisos', label: 'Avisos', icon: <MessageCircle className="h-4 w-4" /> }] : []),
    { id: 'mensajes', label: 'Mensajes', icon: <MessageSquare className="h-4 w-4" />, count: messages.length }
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-3 sm:p-5">
      <div className="client-portal-hero rounded-2xl bg-gradient-to-r from-navy-900 via-navy to-sky p-5 text-white shadow-soft sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-extrabold sm:text-2xl">{isServicesOnly ? 'Mis servicios MARKETOS' : 'Mi Proyecto'}</h1>
            <p className="mt-1 max-w-lg text-sm text-white/75">
              {isServicesOnly
                ? `Aquí puedes seguir lo que MARKETOS está creando para ${data?.client?.name || 'tu negocio'} y descubrir nuevas soluciones.`
                : `Todo lo que MARKETOS está construyendo para ${data?.client?.name || 'tu negocio'}. Pide lo que necesites, nosotros lo hacemos.`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="soft" className="client-portal-hero__social" icon={<CalendarDays className="h-4 w-4" />} onClick={openMarketosSocial}>
              Abrir MARKETOS Social
            </Button>
            <Button variant="soft" className="client-portal-hero__help" icon={<Sparkles className="h-4 w-4" />} onClick={() => openAssistant()}>
              Pedir ayuda guiada
            </Button>
            <Button variant="primary" className="client-portal-hero__change" icon={<Send className="h-4 w-4" />} onClick={() => { setTab('productos'); setNotice({ tone: 'info', text: 'Elige el producto contratado que quieres ajustar y luego pulsa “Pedir un cambio”.' }); }}>
              Ver mis productos
            </Button>
          </div>
        </div>
      </div>

      {notice && <Alert tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Alert>}

      {/* Lo más urgente para el cliente: si devolvimos algo, tiene que verlo
          antes que cualquier otra cosa, no enterrado en una pestaña. */}
      {needsMyAnswer.length > 0 && (
        <Alert tone="warning" title="Necesitamos un dato tuyo">
          Tienes {needsMyAnswer.length === 1 ? 'una solicitud' : `${needsMyAnswer.length} solicitudes`} esperando tu respuesta.{' '}
          <button type="button" className="font-bold underline" onClick={() => { setTab('solicitudes'); setDetailRequest(needsMyAnswer[0]); }}>
            Ver ahora
          </button>
        </Alert>
      )}

      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      <AsyncBoundary loading={loading} error={error} onRetry={load} loadingLabel="Cargando tu proyecto…">
        <div className="animate-fade-in">
          {tab === 'resumen' && (
            <div className="space-y-5">
              <StatGrid>
                <StatCard label="Productos activos" value={services.filter((s) => s.status === 'active').length} tone="navy" onClick={() => setTab('productos')} />
                <StatCard label="Solicitudes abiertas" value={metrics.pending_requests ?? openRequests.length} tone="gold" onClick={() => setTab('solicitudes')} />
                <StatCard label="Cambios ya hechos" value={metrics.completed_changes ?? 0} tone="green" />
                {isServicesOnly
                  ? <StatCard label="Páginas activas" value={metrics.sites ?? 0} tone="sky" onClick={() => setTab('metricas')} />
                  : <StatCard label="Contactos" value={metrics.contacts ?? 0} tone="sky" onClick={() => setTab('metricas')} />}
              </StatGrid>

              <div className="grid gap-5 lg:grid-cols-2">
                <Card>
                  <CardHeader icon={<Package className="h-5 w-5" />} title="Lo que tienes con nosotros" />
                  <div className="space-y-2 p-4">
                    {services.length === 0
                      ? <p className="text-sm text-navy-900/55">Estamos preparando tu información.</p>
                      : services.slice(0, 4).map((service) => (
                        <div key={service.assignment_id} className="flex items-center justify-between gap-3 rounded-xl bg-cream p-3">
                          <p className="min-w-0 truncate font-semibold text-navy-900">{service.name}</p>
                          <Badge tone={SERVICE_STATUS[service.status]?.tone || 'sky'}>
                            {SERVICE_STATUS[service.status]?.label || service.status}
                          </Badge>
                        </div>
                      ))}
                  </div>
                </Card>

                <Card>
                  <CardHeader icon={<ClipboardList className="h-5 w-5" />} title="Tus solicitudes en curso" />
                  <div className="space-y-2 p-4">
                    {openRequests.length === 0
                      ? <p className="text-sm text-navy-900/55">No tienes solicitudes abiertas.</p>
                      : openRequests.slice(0, 4).map((request) => (
                        <button
                          key={request.id}
                          type="button"
                          onClick={() => setDetailRequest(request)}
                          className="flex w-full items-center justify-between gap-3 rounded-xl bg-cream p-3 text-left hover:bg-navy-50"
                        >
                          <p className="min-w-0 truncate font-semibold text-navy-900">{request.title}</p>
                          <Badge tone={REQUEST_STATUS[request.status]?.tone}>{REQUEST_STATUS[request.status]?.clientLabel}</Badge>
                        </button>
                      ))}
                  </div>
                </Card>
              </div>
            </div>
          )}

          {tab === 'productos' && (
            <div className="grid gap-4 sm:grid-cols-2">
              {services.length === 0 && (
                <Card className="sm:col-span-2">
                  <div className="p-8 text-center text-sm text-navy-900/55">Estamos preparando tu información. En breve verás aquí todo lo que tienes contratado.</div>
                </Card>
              )}
              {services.map((service) => (
                <Card key={service.assignment_id} className="overflow-hidden">
                  {service.image_url && <img src={service.image_url} alt="" className="h-32 w-full object-cover" loading="lazy" />}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-bold text-navy-900">{service.name}</h3>
                      <Badge tone={SERVICE_STATUS[service.status]?.tone || 'sky'}>
                        {SERVICE_STATUS[service.status]?.label || service.status}
                      </Badge>
                    </div>
                    <p className="mt-1.5 text-sm text-navy-900/65">{service.description}</p>

                    {service.progress && (
                      <div className="mt-3 rounded-xl bg-sky-50 p-3">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-sky">Avance actual</p>
                        <p className="mt-0.5 text-sm font-bold text-navy-900">{service.progress.label}</p>
                        {service.progress.detail && <p className="mt-1 text-xs text-navy-900/60">{service.progress.detail}</p>}
                        {service.web_project && (
                          <div className="mt-3">
                            <div className="mb-1 flex items-center justify-between text-[11px] font-bold text-navy-900/55">
                              <span>Progreso</span>
                              <span>{WEB_PROJECT_PROGRESS[service.web_project.status] ?? 10}%</span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-white">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-magenta to-sky transition-all"
                                style={{ width: `${WEB_PROJECT_PROGRESS[service.web_project.status] ?? 10}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {service.contract && (
                      <div className="mt-3 grid gap-2 rounded-xl border border-navy-50 p-3 text-xs sm:grid-cols-2">
                        <div>
                          <p className="font-bold uppercase tracking-wide text-navy-900/40">Modalidad</p>
                          <p className="mt-0.5 font-semibold text-navy-900">{service.contract.plan_label}</p>
                        </div>
                        <div>
                          <p className="font-bold uppercase tracking-wide text-navy-900/40">Valor contratado</p>
                          <p className="mt-0.5 font-semibold text-navy-900">
                            {formatMoney(service.agreed_price ?? service.contract.amount)} {BILLING_CYCLE_LABEL[service.contract.billing_cycle] || ''}
                          </p>
                        </div>
                        {service.contract.minimum_term_months > 0 && (
                          <div>
                            <p className="font-bold uppercase tracking-wide text-navy-900/40">Permanencia inicial</p>
                            <p className="mt-0.5 font-semibold text-navy-900">{service.contract.minimum_term_months} meses</p>
                          </div>
                        )}
                        {service.contract.annual_renewal != null && (
                          <div>
                            <p className="font-bold uppercase tracking-wide text-navy-900/40">Renovación anual</p>
                            <p className="mt-0.5 font-semibold text-navy-900">{formatMoney(service.contract.annual_renewal)} · hosting y dominio</p>
                          </div>
                        )}
                      </div>
                    )}

                    {service.website && (service.website.domain_name || service.website.publication_url) && (
                      <div className="mt-3 rounded-xl bg-cream p-3 text-xs text-navy-900/70">
                        {service.website.domain_name && (
                          <p><span className="font-bold">Dominio:</span> {service.website.domain_name} · {DOMAIN_STATUS_LABEL[service.website.domain_status] || service.website.domain_status}</p>
                        )}
                        {safeHttpUrl(service.website.publication_url) && (
                          <a className="mt-1 inline-flex items-center gap-1 font-bold text-sky hover:underline" href={safeHttpUrl(service.website.publication_url)} target="_blank" rel="noreferrer">
                            <Globe className="h-3.5 w-3.5" /> Ver mi página
                          </a>
                        )}
                      </div>
                    )}

                    {service.web_project?.preview_path && !safeHttpUrl(service.website?.publication_url) && (
                      <a
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-navy-900 px-3 py-2 text-xs font-bold text-white hover:bg-navy"
                        href={`${API_URL}${service.web_project.preview_path}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Globe className="h-3.5 w-3.5" /> Revisar mi página
                      </a>
                    )}

                    {service.deliverables?.length > 0 && (
                      <div className="mt-3">
                        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-navy-900/45">Lo que incluye</p>
                        <ul className="space-y-1">
                          {service.deliverables.map((item, index) => (
                            <li key={index} className="flex gap-2 text-sm text-navy-900/70">
                              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="mt-4 flex items-center justify-between border-t border-navy-50 pt-3">
                      <span className="text-xs text-navy-900/50">Desde {formatDate(service.starts_at)}</span>
                      <Button
                        variant="soft" size="sm"
                        onClick={() => { setForm({ ...EMPTY_FORM, service_id: String(service.assignment_id) }); setFormOpen(true); }}
                      >
                        Pedir un cambio
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {tab === 'catalogo' && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-sky-100 bg-sky-50 p-4">
                <h2 className="font-bold text-navy-900">Soluciones que también pueden ayudarte</h2>
                <p className="mt-1 text-sm text-navy-900/65">Elige la que más se parece a lo que necesitas y conversemos por WhatsApp. Te orientaremos con claridad y sin compromiso.</p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {availableServices.length === 0 && (
                  <Card className="sm:col-span-2 lg:col-span-3">
                    <div className="p-8 text-center text-sm text-navy-900/55">Por ahora ya tienes todos los servicios disponibles en el catálogo.</div>
                  </Card>
                )}
                {availableServices.map((service) => {
                  const presentation = getServicePresentation(service);
                  const imageUrl = safeHttpUrl(service.image_url) || presentation.image;
                  return (
                    <Card key={service.id} className="group flex h-full flex-col overflow-hidden transition duration-300 hover:-translate-y-1 hover:shadow-soft">
                      <div className="relative h-44 overflow-hidden bg-navy-50">
                        <img
                          src={imageUrl}
                          alt={`${service.name}: una solución para tu negocio`}
                          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/85 via-navy-900/10 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-sky-100">{presentation.eyebrow}</p>
                          <h3 className="mt-1 text-lg font-extrabold leading-tight">{service.name}</h3>
                        </div>
                        {service.featured && <Badge tone="gold" className="absolute right-3 top-3 shadow-card">Recomendado</Badge>}
                      </div>

                      <div className="flex flex-1 flex-col p-4">
                        <p className="text-sm font-bold leading-relaxed text-navy-900">{presentation.promise}</p>
                        <p className="mt-2 text-sm leading-relaxed text-navy-900/65">{presentation.result}</p>

                        {service.benefits?.length > 0 && (
                          <div className="mt-4 rounded-xl bg-emerald-50/70 p-3">
                            <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Lo que cambia para ti</p>
                            <ul className="space-y-1.5">
                              {service.benefits.slice(0, 3).map((benefit, index) => (
                                <li key={index} className="flex gap-2 text-xs leading-relaxed text-navy-900/75">
                                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> {benefit}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="mt-auto pt-4">
                          <div className="mb-3 flex items-end justify-between gap-3 border-t border-navy-50 pt-3">
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wide text-navy-900/40">Inversión desde</p>
                              <p className="text-lg font-extrabold text-navy-900">{formatMoney(service.base_price)}</p>
                            </div>
                            <span className="text-xs font-semibold text-navy-900/45">Con asesoría MARKETOS</span>
                          </div>
                          <a
                            href={serviceWhatsAppUrl(service, data?.client?.name || clientData?.name)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-magenta px-4 py-3 text-sm font-bold text-white transition hover:bg-magenta-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-magenta"
                            aria-label={`${presentation.cta} por WhatsApp`}
                          >
                            <MessageCircle className="h-4 w-4" />
                            {presentation.cta}
                            <ArrowRight className="h-4 w-4" />
                          </a>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {tab === 'solicitudes' && (
            <Card>
              <CardHeader
                icon={<ClipboardList className="h-5 w-5" />}
                title="Mis solicitudes"
                subtitle="Aquí ves en qué punto está cada cosa que nos has pedido."
                actions={<Button size="sm" icon={<Package className="h-4 w-4" />} onClick={() => { setTab('productos'); setNotice({ tone: 'info', text: 'Elige el producto contratado que quieres ajustar y luego pulsa “Pedir un cambio”.' }); }}>Mis productos</Button>}
              />
              <div className="space-y-2 p-4">
                {requests.length === 0
                  ? <p className="py-6 text-center text-sm text-navy-900/55">Todavía no nos has pedido nada. Cuando lo hagas, aparecerá aquí.</p>
                  : requests.map((request) => (
                    <button
                      key={request.id}
                      type="button"
                      onClick={() => setDetailRequest(request)}
                      className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-navy-50 p-3.5 text-left transition hover:border-navy-100 hover:bg-cream"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-navy-900">{request.title}</p>
                        <p className="mt-0.5 text-xs text-navy-900/50">
                          {formatDate(request.created_at)}
                          {request.service_name ? ` · ${request.service_name}` : ''}
                        </p>
                      </div>
                      <Badge tone={REQUEST_STATUS[request.status]?.tone}>
                        {REQUEST_STATUS[request.status]?.clientLabel || request.status}
                      </Badge>
                    </button>
                  ))}
              </div>
            </Card>
          )}

          {tab === 'metricas' && (
            <div className="space-y-4">
              <StatGrid>
                {!isServicesOnly && <StatCard label="Contactos" value={metrics.contacts ?? 0} tone="sky" />}
                {!isServicesOnly && <StatCard label="Ventas" value={metrics.sales ?? 0} tone="green" />}
                {!isServicesOnly && <StatCard label="Ingresos" value={formatMoney(metrics.revenue)} tone="magenta" />}
                <StatCard label="Páginas activas" value={metrics.sites ?? 0} tone="navy" icon={<Globe className="h-4 w-4" />} />
                {!isServicesOnly && <StatCard label="Campañas activas" value={metrics.campaigns ?? 0} tone="gold" />}
                <StatCard label="Solicitudes abiertas" value={metrics.pending_requests ?? 0} tone="gold" />
                <StatCard label="Cambios completados" value={metrics.completed_changes ?? 0} tone="green" />
                <StatCard label="Productos contratados" value={services.length} tone="grape" />
              </StatGrid>
              <p className="text-xs text-navy-900/45">
                Estos números corresponden únicamente a tu negocio.
              </p>
            </div>
          )}

          {tab === 'ia' && hasActiveCrmAutomation && (
            <Card className="overflow-hidden border-sky-100 bg-gradient-to-br from-white via-sky-50 to-navy-50">
              <CardHeader
                icon={<KeyRound className="h-5 w-5" />}
                title="Conecta tu IA"
                subtitle="Ingresa una sola vez la API key de tu proveedor. MARKETOS la usará cuando configure automatizaciones de IA para tu negocio."
              />
              <div className="space-y-4 p-4 sm:p-5">
                {aiConnection === null ? (
                  <p className="text-sm text-navy-900/55">Estamos revisando si ya tienes una IA conectada…</p>
                ) : (
                  <>
                    <Alert tone={aiConnection.connected ? 'success' : 'info'} title={aiConnection.connected ? 'Tu IA está conectada' : 'Aún no has conectado una IA'}>
                      {aiConnection.connected
                        ? 'Tu clave está guardada de forma segura. No la mostramos nuevamente y las automatizaciones de IA que configure MARKETOS podrán usarla.'
                        : 'Cuando tengas una API key de tu proveedor, pégala aquí. No tendrás que volver a ingresarla en cada automatización.'}
                    </Alert>
                    <Field
                      label={aiConnection.connected ? 'Reemplazar API key' : 'Tu API key'}
                      required
                      hint="La guardamos cifrada y nunca volveremos a mostrarla en pantalla."
                    >
                      <Input
                        type="password"
                        autoComplete="new-password"
                        value={aiApiKey}
                        onChange={(event) => setAiApiKey(event.target.value)}
                        placeholder="Pega aquí tu API key"
                      />
                    </Field>
                    <div className="rounded-xl border border-gold-100 bg-gold-50 p-3 text-sm text-navy-900/70">
                      La opción <strong>Probar conexión</strong> realiza una consulta mínima a tu proveedor de IA y puede consumir una cantidad pequeña de los créditos de tu propia API key.
                    </div>
                    <div className="flex flex-wrap justify-between gap-3 border-t border-sky-100 pt-4">
                      <div className="flex flex-wrap gap-2">
                        {aiConnection.connected && (
                          <Button variant="soft" loading={aiBusy} onClick={testAiConnection}>Probar conexión</Button>
                        )}
                        {aiConnection.connected && (
                          <Button variant="danger" disabled={aiBusy} onClick={() => setDisconnectingAi(true)}>Desconectar IA</Button>
                        )}
                      </div>
                      <Button className="!bg-[#102b70] !text-white hover:!bg-[#0a2055]" loading={aiBusy} onClick={saveAiConnection}>
                        {aiConnection.connected ? 'Guardar nueva API key' : 'Guardar API key'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Card>
          )}

          {tab === 'avisos' && hasCrm && (
            <Card className="overflow-hidden border-sky-100 bg-gradient-to-br from-white via-sky-50 to-navy-50">
              <CardHeader
                icon={<MessageCircle className="h-5 w-5" />}
                title="Dónde recibir avisos de tus automatizaciones"
                subtitle="Cuando confirmes una automatización que te avise, MARKETOS usará este WhatsApp. Nunca mostraremos tu número completo aquí."
              />
              <div className="space-y-4 p-4 sm:p-5">
                {!workflowRecipient ? (
                  <p className="text-sm text-navy-900/55">Estamos preparando tu configuración de avisos…</p>
                ) : (
                  <>
                    <Alert tone={workflowRecipient.available ? 'info' : 'warning'} title={workflowRecipient.available ? 'Destino actual' : 'Falta un WhatsApp para avisos'}>
                      {workflowRecipient.available
                        ? `Actualmente se usaría ${workflowRecipient.label}.`
                        : 'No encontramos un número válido con el que se creó tu cuenta. Puedes indicar otro aquí.'}
                    </Alert>
                    <Field label="¿Qué WhatsApp quieres usar para tus próximos avisos?" required>
                      <Select value={recipientMode} onChange={(event) => setRecipientMode(event.target.value)}>
                        <option value="registered">Usar mi WhatsApp de registro</option>
                        <option value="custom">Usar otro WhatsApp</option>
                      </Select>
                    </Field>
                    {recipientMode === 'custom' && (
                      <Field label="Otro WhatsApp" required hint="Incluye el código de país. Ej.: 593...">
                        <Input inputMode="tel" value={customRecipient} onChange={(event) => setCustomRecipient(event.target.value)} placeholder="593999999999" />
                      </Field>
                    )}
                    <div className="rounded-xl border border-navy-100 bg-white/85 p-3 text-sm text-navy-900/70">
                      Antes de crear cada aviso, el asistente te pedirá confirmación: “¿Quieres recibirlo en este WhatsApp?”. Puedes cambiarlo aquí cuando lo necesites.
                    </div>
                    <div className="flex justify-end border-t border-sky-100 pt-4">
                      <Button className="!bg-[#102b70] !text-white hover:!bg-[#0a2055]" loading={busy} onClick={saveWorkflowRecipient}>
                        Guardar destino de avisos
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Card>
          )}

          {tab === 'mensajes' && (
            <Card>
              <CardHeader icon={<MessageSquare className="h-5 w-5" />} title="Mensajes de MARKETOS" subtitle="Avisos y actualizaciones sobre tu proyecto." />
              <div className="space-y-4 p-4">
                <div className="flex gap-2">
                  <Input value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Escríbenos…" />
                  <Button
                    icon={<Send className="h-4 w-4" />}
                    disabled={!reply.trim() || busy}
                    onClick={() => run(async () => { await portalService.reply(reply.trim()); setReply(''); }, 'Mensaje enviado.')}
                  >
                    Enviar
                  </Button>
                </div>
                {messages.length === 0
                  ? <p className="text-sm text-navy-900/55">Todavía no hay mensajes.</p>
                  : messages.map((message) => (
                    <div key={message.id} className={`rounded-xl p-3.5 ${message.author_type === 'client' ? 'bg-navy-50' : 'bg-sky-50'}`}>
                      <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-navy-900/50">
                        {message.author_type === 'client' ? 'Tú' : 'MARKETOS'} · {formatDateTime(message.created_at)}
                      </p>
                      <p className="whitespace-pre-wrap text-sm text-navy-900">{message.body}</p>
                    </div>
                  ))}
              </div>
            </Card>
          )}
        </div>
      </AsyncBoundary>

      <Modal
        open={assistantOpen}
        onClose={closeAssistant}
        size="lg"
        title="Cuéntanos qué necesitas"
        subtitle="Te haremos solo las preguntas necesarias para preparar una propuesta a tu medida."
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={closeAssistant}>Cancelar</Button>
            {!assistantServiceId
              ? null
              : !assistantIntake
              ? <Button className="bg-[#e5007d] text-white shadow-md hover:bg-[#bd0068]" loading={busy} onClick={prepareAssistantForm} icon={<Sparkles className="h-4 w-4" />}>Continuar</Button>
              : <>
                {assistantStep > 0 && (
                  <Button variant="soft" disabled={busy} onClick={() => { setNotice(null); setAssistantStep((step) => Math.max(step - 1, 0)); }} icon={<ChevronLeft className="h-4 w-4" />}>
                    Anterior
                  </Button>
                )}
                {assistantStep < assistantFields.length - 1
                  ? <Button className="bg-[#0b8fd3] text-white shadow-md hover:bg-[#0874ae]" loading={busy} onClick={goToNextAssistantStep} icon={<ArrowRight className="h-4 w-4" />}>Siguiente</Button>
                  : <Button className="bg-[#e5007d] text-white shadow-md hover:bg-[#bd0068]" loading={busy} onClick={submitAssistantIntake} icon={<Send className="h-4 w-4" />}>Preparar propuesta</Button>}
              </>}
          </>
        }
      >
        {!assistantServiceId ? (
          <div className="space-y-4">
            <Alert tone="info" title="Primero elige el producto que quieres ajustar">
              La ayuda guiada solo puede preparar cambios para productos que ya tienes contratados. Si necesitas algo diferente, podrás explorar otras soluciones antes de enviar una solicitud.
            </Alert>
            {services.length === 0 ? (
              <div className="rounded-xl border border-navy-100 bg-cream p-4 text-sm text-navy-900/70">
                Aún no tienes productos disponibles para ajustar. Explora nuestras soluciones o escribe a MARKETOS.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {services.map((service) => (
                  <button
                    key={service.assignment_id}
                    type="button"
                    onClick={() => { setAssistantServiceId(String(service.assignment_id)); setAssistantSubmissionError(''); }}
                    className="rounded-xl border border-sky-100 bg-gradient-to-br from-white to-sky-50 p-4 text-left transition hover:border-sky-300 hover:shadow-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <Package className="h-5 w-5 text-sky" />
                      <Badge tone={SERVICE_STATUS[service.status]?.tone || 'sky'}>{SERVICE_STATUS[service.status]?.label || service.status}</Badge>
                    </div>
                    <p className="mt-3 font-bold text-navy-900">{service.name}</p>
                    <p className="mt-1 line-clamp-2 text-sm text-navy-900/60">{service.description || 'Solicita un ajuste relacionado con este producto.'}</p>
                    <p className="mt-3 text-xs font-bold text-sky">Solicitar cambio en este producto →</p>
                  </button>
                ))}
              </div>
            )}
            {assistantSubmissionError && (
              <Alert tone="warning" title="Este cambio no está disponible en el producto elegido">
                {assistantSubmissionError}
                <div className="mt-3">
                  <Button size="sm" variant="soft" onClick={() => { closeAssistant(); setTab('catalogo'); }}>Explorar productos</Button>
                </div>
              </Alert>
            )}
          </div>
        ) : !assistantIntake ? (
          <div className="space-y-4">
            <Alert tone="info" title="Tú decides antes de que se haga algo">
              Primero entendemos lo que necesitas. Después MARKETOS revisará y aprobará la propuesta antes de aplicar cualquier cambio.
            </Alert>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-sky-100 bg-sky-50 p-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-sky">Producto seleccionado</p>
                <p className="mt-0.5 font-bold text-navy-900">{assistantService?.name || 'Producto contratado'}</p>
              </div>
              <Button size="sm" variant="soft" disabled={busy} onClick={() => { setAssistantServiceId(''); setAssistantSubmissionError(''); }}>Cambiar</Button>
            </div>
            {assistantSubmissionError && (
              <Alert tone="warning" title="Este cambio no está disponible en el producto elegido">
                {assistantSubmissionError}
                <div className="mt-3">
                  <Button size="sm" variant="soft" onClick={() => { closeAssistant(); setTab('catalogo'); }}>Explorar productos</Button>
                </div>
              </Alert>
            )}
            <Field label="¿Qué quieres crear o cambiar?" required>
              <Textarea rows={5} value={assistantGoal} onChange={(event) => setAssistantGoal(event.target.value)} placeholder="Ej.: Quiero un formulario para que los interesados en una cita entren a mi proceso de seguimiento y reciban un mensaje de bienvenida." />
            </Field>
          </div>
        ) : (
          <div className="space-y-4">
            <Alert tone="info" title={assistantIntake.requirement_form?.title || 'Cuéntanos un poco más'}>
              {assistantIntake.requirement_form?.intro || 'Con estos datos podremos preparar una propuesta.'}
            </Alert>
            {assistantSubmissionError && (
              <Alert tone="warning" title="Aún no pudimos preparar la propuesta">
                {assistantSubmissionError} Puedes ajustar tus respuestas y volver a intentarlo; no se perdió lo que escribiste.
              </Alert>
            )}
            {currentAssistantField && (
              <>
                <div className="rounded-xl border border-sky-100 bg-sky-50 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-bold text-sky-700">Pregunta {assistantStep + 1} de {assistantFields.length}</p>
                    <span className="text-xs font-semibold text-sky-700/70">Paso a paso</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-white" aria-hidden="true">
                    <div className="h-full rounded-full bg-gradient-to-r from-sky to-magenta transition-all" style={{ width: `${((assistantStep + 1) / assistantFields.length) * 100}%` }} />
                  </div>
                </div>
                <Field label={currentAssistantField.label} required={currentAssistantField.required} hint={currentAssistantField.hint}>
                  {currentAssistantField.type === 'select' ? (
                    <div className="space-y-3">
                      <Select
                        value={assistantAnswers[currentAssistantField.key] || ''}
                        onChange={(event) => setAssistantAnswers({ ...assistantAnswers, [currentAssistantField.key]: event.target.value })}
                      >
                        <option value="">Selecciona una opción</option>
                        {(currentAssistantField.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
                        <option value={CUSTOM_ANSWER}>Otra respuesta personalizada…</option>
                      </Select>
                      {assistantAnswers[currentAssistantField.key] === CUSTOM_ANSWER && (
                        <Textarea
                          rows={3}
                          value={assistantCustomAnswers[currentAssistantField.key] || ''}
                          onChange={(event) => setAssistantCustomAnswers({ ...assistantCustomAnswers, [currentAssistantField.key]: event.target.value })}
                          placeholder="Escribe exactamente cómo quieres que funcione…"
                        />
                      )}
                    </div>
                  ) : currentAssistantField.type === 'textarea' ? (
                    <Textarea
                      rows={5}
                      value={assistantAnswers[currentAssistantField.key] ?? defaultAssistantAnswer(currentAssistantField)}
                      onChange={(event) => setAssistantAnswers({ ...assistantAnswers, [currentAssistantField.key]: event.target.value })}
                    />
                  ) : (
                    <Input
                      value={assistantAnswers[currentAssistantField.key] || ''}
                      onChange={(event) => setAssistantAnswers({ ...assistantAnswers, [currentAssistantField.key]: event.target.value })}
                    />
                  )}
                </Field>
                {assistantFieldExample(currentAssistantField) && (
                  <div className="rounded-xl border border-navy-100 bg-cream px-3.5 py-3 text-sm text-navy-900/75">
                    <span className="font-bold text-navy-900">Ejemplo de respuesta: </span>
                    {assistantFieldExample(currentAssistantField)}
                  </div>
                )}
                {currentAssistantField.type === 'textarea' && defaultAssistantAnswer(currentAssistantField) && (
                  <p className="rounded-xl border border-grape-100 bg-grape-50 px-3 py-2 text-xs font-medium text-grape">
                    ✨ La IA preparó un borrador editable. Ajusta lo que necesites; los datos entre paréntesis son para personalizar.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ─── Formulario de solicitud (spec §5) ─── */}
      <Modal
        open={formOpen}
        onClose={() => { setFormOpen(false); setFormErrors({}); }}
        size="lg"
        title="Pedir un cambio"
        subtitle="Cuéntanos qué necesitas y nos encargamos."
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={submitRequest} loading={busy} icon={<Send className="h-4 w-4" />}>Enviar solicitud</Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Aviso exigido por la spec: visible ANTES de enviar, no después. */}
          <Alert tone="info" title="Cómo funciona">{AVISO_APROBACION}</Alert>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="¿Sobre qué producto?" required error={formErrors.service_id} hint="Selecciona el producto contratado que quieres ajustar.">
              <Select value={form.service_id} onChange={(event) => setForm({ ...form, service_id: event.target.value })}>
                <option value="">Selecciona un producto</option>
                {services.map((service) => <option key={service.assignment_id} value={service.assignment_id}>{service.name}</option>)}
              </Select>
            </Field>
            <Field label="¿Qué tipo de cambio es?">
              <Select value={form.change_type} onChange={(event) => setForm({ ...form, change_type: event.target.value })}>
                {CHANGE_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
              </Select>
            </Field>
          </div>

          <Field label="¿Qué quieres cambiar?" required error={formErrors.title}>
            <Input
              value={form.title}
              onChange={(event) => setForm({ ...form, title: event.target.value })}
              placeholder="Ej.: Cambiar el texto del botón principal"
            />
          </Field>

          <Field label="Cuéntanos cómo te gustaría que quedara" required error={formErrors.description}>
            <Textarea
              rows={5}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              placeholder="Mientras más detalles nos des, más rápido lo hacemos bien a la primera."
            />
          </Field>

          <Field label="¿Por qué lo necesitas?" hint="Nos ayuda a proponerte la mejor solución.">
            <Textarea rows={2} value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="¿Qué tan urgente es?">
              <Select value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })}>
                {PRIORITIES.map((priority) => <option key={priority.value} value={priority.value}>{priority.label}</option>)}
              </Select>
            </Field>
            <Field label="¿Para cuándo lo necesitas?" hint="Opcional. Haremos lo posible.">
              <Input type="date" value={form.desired_date} onChange={(event) => setForm({ ...form, desired_date: event.target.value })} />
            </Field>
          </div>

          <Field label="Enlaces de referencia" hint="Pega aquí enlaces a imágenes o documentos, uno por línea.">
            <Textarea rows={2} value={form.attachments} onChange={(event) => setForm({ ...form, attachments: event.target.value })} placeholder="https://…" />
          </Field>
        </div>
      </Modal>

      {/* ─── Detalle de una solicitud ─── */}
      <Modal
        open={!!detailRequest}
        onClose={() => { setDetailRequest(null); setReply(''); }}
        title={detailRequest?.title}
        subtitle={detailRequest ? `Enviada el ${formatDate(detailRequest.created_at)}` : undefined}
        footer={detailRequest && ['pending', 'reviewing', 'returned'].includes(detailRequest.status) && (
          <Button variant="danger" icon={<X className="h-4 w-4" />} onClick={() => setCancelling(detailRequest)}>
            Cancelar solicitud
          </Button>
        )}
      >
        {detailRequest && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={REQUEST_STATUS[detailRequest.status]?.tone}>
                {REQUEST_STATUS[detailRequest.status]?.clientLabel || detailRequest.status}
              </Badge>
              {detailRequest.service_name && <Badge tone="navy"><Package className="h-3 w-3" /> {detailRequest.service_name}</Badge>}
              {detailRequest.desired_date && <Badge tone="sky"><CalendarDays className="h-3 w-3" /> {formatDate(detailRequest.desired_date)}</Badge>}
            </div>

            <div>
              <SectionTitle>Lo que pediste</SectionTitle>
              <p className="whitespace-pre-wrap rounded-xl bg-cream p-4 text-sm text-navy-900/80">{detailRequest.description}</p>
            </div>

            {detailRequest.status === 'returned' && detailRequest.returned_reason && (
              <>
                <Alert tone="warning" title="Necesitamos que nos aclares algo">{detailRequest.returned_reason}</Alert>
                <div className="flex gap-2">
                  <Input value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Escribe tu respuesta…" />
                  <Button
                    disabled={!reply.trim() || busy}
                    onClick={() => run(
                      async () => { await portalService.reply(reply.trim(), detailRequest.id); setReply(''); setDetailRequest(null); },
                      'Gracias. Ya lo estamos revisando.'
                    )}
                  >
                    Responder
                  </Button>
                </div>
              </>
            )}

            {detailRequest.status === 'rejected' && detailRequest.rejected_reason && (
              <Alert tone="danger" title="No pudimos hacerlo">{detailRequest.rejected_reason}</Alert>
            )}

            {detailRequest.status === 'completed' && (
              <Alert tone="success" title="Listo">
                Aplicamos el cambio el {formatDate(detailRequest.executed_at)}. Si algo no quedó como esperabas, pídenos un ajuste.
              </Alert>
            )}

            {['pending', 'reviewing'].includes(detailRequest.status) && (
              <Alert tone="info"><BadgeCheck className="mr-1 inline h-4 w-4" />{AVISO_APROBACION}</Alert>
            )}

            {detailRequest.status === 'approved' && (
              <Alert tone="info">Aprobada. Nuestro equipo ya la tiene en cola y te avisaremos cuando esté lista.</Alert>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!cancelling}
        loading={busy}
        title="Cancelar solicitud"
        confirmLabel="Sí, cancelar"
        cancelLabel="No, mantenerla"
        tone="danger"
        onCancel={() => setCancelling(null)}
        onConfirm={() => run(
          async () => { await portalService.cancelRequest(cancelling.id); setCancelling(null); setDetailRequest(null); },
          'Solicitud cancelada.'
        )}
        message="Dejaremos de trabajar en esta petición. Puedes volver a enviarla cuando quieras."
      />

      <ConfirmDialog
        open={disconnectingAi}
        loading={aiBusy}
        title="Desconectar tu IA"
        confirmLabel="Sí, desconectar"
        cancelLabel="No, mantenerla"
        tone="danger"
        onCancel={() => setDisconnectingAi(false)}
        onConfirm={disconnectAiConnection}
        message="Las automatizaciones de IA dejarán de usar esta API key hasta que conectes otra. Esta acción no elimina tus automatizaciones."
      />
    </div>
  );
}
